"use client";

import { googleAnalyticsId, googleTagManagerId, scriptarioAnalyticsMode } from "@/lib/config";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let eventSequence = 0;

const SCRIPTARIO_GTM_EVENT_FIELDS = [
  "site_id",
  "locale",
  "market",
  "environment",
  "app_env",
  "page_path",
  "page_location",
  "page_title",
  "public_path",
  "timestamp",
  "session_id",
  "place",
  "tariff_name",
  "duration_seconds",
  "file_type",
  "platform",
  "depth",
  "billing",
  "source",
  "method",
  "report_type",
  "amount",
  "currency",
  "auth_mode",
  "minutes",
  "error_type",
  "item",
  "action",
  "column",
  "direction",
  "query_length",
  "count",
  "tab",
  "format",
  "seek_position",
  "position",
  "speed",
  "from_position",
  "to_position",
  "success",
  "period",
  "plan_name",
  "has_discount",
  "discount_percent",
  "phase",
  "step",
  "page",
  "state",
  "file_size_bytes",
  "audio_language",
  "file_format",
  "media_type",
  "processing_time_ms",
  "status",
  "payment_provider",
  "plan_id",
  "billing_period",
  "transaction_id",
  "event_id",
  "transport_type",
] as const;

// GA4 трактует эти имена параметров события как ручное переопределение
// источника трафика сессии: событие с `source: 'email'` переписывало
// `sessionSource` и стирало `google / cpc` у рекламной сессии. GTM-тег
// контейнера маппит `source` в GA4 как есть, поправить контейнер мы отсюда
// не можем, поэтому переименовываем на стороне отправителя — `place` уже
// есть в карте параметров тега и не зарезервирован. `session_id`
// зарезервирован под идентификатор сессии GA4, наш случайный base36 там
// не нужен. Для Яндекс.Метрики форма событий не меняется.
const GA4_RESERVED_PARAM_RENAMES: Record<string, string> = { source: "place" };
const GA4_DROPPED_PARAMS = ["session_id"];

function isReservedGA4Param(field: string) {
  return GA4_DROPPED_PARAMS.includes(field)
    || Object.prototype.hasOwnProperty.call(GA4_RESERVED_PARAM_RENAMES, field);
}

// Шаблон обнуления строится ТЕМ ЖЕ фильтром, что и payload.
//
// Пока это были два независимых списка, они разошлись, и фильтр выше оказался
// бесполезным: `session_id` и `source` стоят в SCRIPTARIO_GTM_EVENT_FIELDS,
// поэтому обнуление возвращало их обратно как `null` сразу после того, как
// отправитель их вычистил. А `null` GTM отдаёт в GA4 ПУСТЫМ параметром, не
// отсутствующим.
//
// Замер живого прода 08.10.2026 (Playwright, scriptario.com): в каждом хите
// `analytics.google.com/g/collect` ехало `sid=` пустым и `ep.source=` пустым,
// причём `gclid` в `dl` был цел. То есть параметры кампании до GA4 доезжали, а
// опереть на них сессию GA4 не мог: за 07-08.10 `session_start` разошлись как
// 172 Unassigned против 97 Paid Search.
const SCRIPTARIO_GTM_CLEARED_FIELDS = SCRIPTARIO_GTM_EVENT_FIELDS
  .filter((field) => !isReservedGA4Param(field));

function toScriptarioPayload(payload: Record<string, unknown>) {
  const result: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (GA4_DROPPED_PARAMS.includes(key)) return;
    const renamed = GA4_RESERVED_PARAM_RENAMES[key];
    // Переименование не должно затирать параметр, который событие уже прислало.
    if (renamed && result[renamed] === undefined && payload[renamed] === undefined) {
      result[renamed] = value;
      return;
    }
    if (renamed) return;
    result[key] = value;
  });
  return result;
}

// Без них GA4 получает пустые dl/dp/dt, и событие, открывающее новую сессию,
// оставляет её без URL и реферера — источник `(not set)`, канал Unassigned.
function currentPageParams() {
  const pageLocation = typeof window === "undefined" ? undefined : window.location;
  if (!pageLocation) return {};
  return {
    page_location: pageLocation.href,
    page_path: pageLocation.pathname + pageLocation.search,
    page_title: typeof document === "undefined" ? undefined : document.title,
  };
}

function createScriptarioEventId(eventName: string) {
  eventSequence += 1;
  const randomPart = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : Math.random().toString(36).slice(2);

  return `${eventName}:${Date.now().toString(36)}:${eventSequence.toString(36)}:${randomPart}`;
}

/** Send one normalized Scriptario event through the configured browser runtime. */
export function sendScriptarioAnalyticsEvent(
  eventName: string,
  payload: Record<string, unknown>,
) {
  if (typeof window === "undefined") return false;

  const eventPayload = {
    ...currentPageParams(),
    ...toScriptarioPayload(payload),
    market: "scriptario",
    event_id: createScriptarioEventId(eventName),
  };

  if (scriptarioAnalyticsMode === "gtm" && googleTagManagerId) {
    try {
      if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
      const clearedFields = Object.fromEntries(
        SCRIPTARIO_GTM_CLEARED_FIELDS.map((field) => [field, null]),
      );
      window.dataLayer.push({
        ...clearedFields,
        ...eventPayload,
        event: eventName,
      });
      return true;
    } catch (error) {
      console.error("[GTM]", eventName, error);
      return false;
    }
  }

  if (scriptarioAnalyticsMode === "ga4" && googleAnalyticsId && typeof window.gtag === "function") {
    try {
      window.gtag("event", eventName, eventPayload);
      return true;
    } catch (error) {
      console.error("[GA4]", eventName, error);
      return false;
    }
  }

  return false;
}
